import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../utils/api';

const SettingsContext = createContext();

// DEFAULT IMAGES - From environment variables for deployment flexibility, with fallbacks
const DEFAULT_HERO_IMAGE_URL = process.env.REACT_APP_DEFAULT_HERO_IMAGE || 'https://customer-assets.emergentagent.com/job_marissa-imagefix/artifacts/6q4ungsx_image-37.png';

// Default settings object - used immediately on first render
// Logo is NOT in settings anymore - it's hardcoded in Navbar/Footer
const DEFAULT_SETTINGS = {
  store_name: '',
  logo_url: '',
  hero_image_url: DEFAULT_HERO_IMAGE_URL,
  contact_email: '',
  contact_phone: '',
  contact_address: '',
};

// Cache key for localStorage
const SETTINGS_CACHE_KEY = 'marissa_settings_cache';
const SETTINGS_CACHE_TIMESTAMP_KEY = 'marissa_settings_cache_timestamp';
const CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes

// Get cached settings synchronously - called once at module load
const getInitialSettings = () => {
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    const timestamp = localStorage.getItem(SETTINGS_CACHE_TIMESTAMP_KEY);
    
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      // Use cache if less than 5 minutes old
      if (age < CACHE_MAX_AGE) {
        const parsed = JSON.parse(cached);
        // Invalidate cache if it has old CDN logo
        if (parsed.logo_url && parsed.logo_url.includes('customer-assets.emergentagent.com')) {
          localStorage.removeItem(SETTINGS_CACHE_KEY);
          localStorage.removeItem(SETTINGS_CACHE_TIMESTAMP_KEY);
          return { settings: DEFAULT_SETTINGS, hasCache: true };
        }
        // Only use if we have actual image URLs
        if (parsed.logo_url || parsed.hero_image_url) {
          return { settings: parsed, hasCache: true };
        }
      }
    }
  } catch (e) {
  }
  // Return default settings with images - NO FLASH!
  return { settings: DEFAULT_SETTINGS, hasCache: true };
};

// Load initial settings synchronously at module level for fastest possible access
const initialCache = getInitialSettings();

export const SettingsProvider = ({ children }) => {
  // Always start with valid settings containing default images
  const [settings, setSettings] = useState(initialCache.settings);
  
  // If we have a valid cache, we're not "loading" - show content immediately
  const [loading, setLoading] = useState(!initialCache.hasCache);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Prevent double fetch in React StrictMode
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      const newSettings = response.data;
      setSettings(newSettings);
      // Cache settings with timestamp for instant load next time
      try {
        localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(newSettings));
        localStorage.setItem(SETTINGS_CACHE_TIMESTAMP_KEY, Date.now().toString());
      } catch (e) {
      }
    } catch (error) {
      // Keep using default settings with images on error
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await api.put('/settings', newSettings);
      setSettings(response.data);
      // Update cache
      try {
        localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(response.data));
      } catch (e) {
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const refreshSettings = async () => {
    await fetchSettings();
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, updateSettings, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
